/* Confirmed-success attribution and conversion tracking for the corporate proposal form. */
(function () {
  'use strict';

  var FORM_NAME = 'corporate_floral_proposal';
  var SUCCESS_MARKER = 'corporate_floral_proposal';
  var DESTINATION_PATH = '/pages/corporate-floral-services';
  var LEAD_PATH = 'corporate_floral_services';
  var SUCCESS_EVENT_NAME = 'bloom_boom:corporate_proposal_success';
  var GA_CONSUMER_URL = 'https://bloom-boom-corporate-ga4-leads.human-renaissance.workers.dev';
  var GA_COOKIE_WAIT_ATTEMPTS = 4;
  var GA_RETRY_LIMIT = 10;
  var GA_RETRY_DELAY_MS = 500;
  var SHOPIFY_PUBLISH_RETRY_LIMIT = 10;
  var SHOPIFY_PUBLISH_RETRY_DELAY_MS = 500;
  var META_RETRY_LIMIT = 10;
  var META_RETRY_DELAY_MS = 500;
  var SUCCESS_URL_CLEANUP_DELAY_MS = 6000;
  var MAX_ATTRIBUTION_LENGTH = 255;
  var MAX_DIMENSION_LENGTH = 64;
  var MAX_DRAFT_VALUE_LENGTH = 10000;
  var ATTRIBUTION_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'fbclid',
    'gclid',
    'gbraid',
    'wbraid'
  ];
  var DIMENSION_FIELDS = {
    service_type: '[data-corp-dimension="service_type"]',
    planning_window: '[data-corp-dimension="planning_window"]',
    headcount_band: '[data-corp-dimension="headcount_band"]',
    budget_band: '[data-corp-dimension="budget_band"]'
  };
  var deliveryStates = window.__bbCorporateProposalDeliveryStates || {};
  window.__bbCorporateProposalDeliveryStates = deliveryStates;

  function storage() {
    try {
      return window.sessionStorage;
    } catch (_error) {
      return null;
    }
  }

  function sessionGet(session, key) {
    try { return session ? session.getItem(key) : null; } catch (_error) { return null; }
  }

  function sessionSet(session, key, value) {
    try { if (session) session.setItem(key, value); } catch (_error) {}
  }

  function sessionRemove(session, key) {
    try { if (session) session.removeItem(key); } catch (_error) {}
  }

  function makeId(prefix) {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return prefix + '_' + window.crypto.randomUUID();
    }
    return prefix + '_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 12);
  }

  function publishShopifyEvent(name, payload, onAccepted, onRejected) {
    if (
      window.Shopify &&
      window.Shopify.analytics &&
      typeof window.Shopify.analytics.publish === 'function'
    ) {
      try {
        var published = window.Shopify.analytics.publish(name, payload);
        if (published && typeof published.then === 'function') {
          published.then(function (dispatched) {
            if (dispatched === true) onAccepted();
            else onRejected();
          }, onRejected);
          return true;
        }
        if (published === true) {
          onAccepted();
          return true;
        }
        /* Shopify's Online Store publishing example is fire-and-forget and does
           not document a return value. Preserve that legacy shape only; explicit
           false, rejected Promises, and thrown errors remain retryable failures. */
        if (typeof published === 'undefined') {
          onAccepted();
          return true;
        }
      } catch (_error) {
        /* Analytics must never interrupt the form experience. */
      }
    }
    return false;
  }

  /* Compatibility transport for the active Reel 13 custom conversion. It is
     intentionally success-only and carries the stable event ID for Meta dedupe. */
  function sendMetaLead(payload, onInvoked) {
    if (typeof window.fbq === 'function') {
      try {
        window.fbq(
          'track',
          'Lead',
          {
            content_name: FORM_NAME,
            content_category: LEAD_PATH
          },
          { eventID: payload.event_id }
        );
        onInvoked();
        return true;
      } catch (_error) {
        return false;
      }
    }
    return false;
  }

  function processingPermission(method) {
    try {
      if (
        !window.Shopify ||
        !window.Shopify.customerPrivacy ||
        typeof window.Shopify.customerPrivacy[method] !== 'function'
      ) return null;
      return window.Shopify.customerPrivacy[method]() === true;
    } catch (_error) {
      return null;
    }
  }

  function applyConsentTerminalStates(state, session, callbacks) {
    var marketingAllowed = processingPermission('marketingAllowed');
    var analyticsAllowed = processingPermission('analyticsProcessingAllowed');
    if (!state.meta && marketingAllowed === false) callbacks.metaSkipped();
    if (!state.ga && analyticsAllowed === false) callbacks.gaSkipped();
    return { marketingAllowed: marketingAllowed, analyticsAllowed: analyticsAllowed };
  }

  function gaCookieValue() {
    var match = String(document.cookie || '').match(/(?:^|;\s*)_ga=([^;]+)/);
    if (!match) return '';
    try {
      return decodeURIComponent(match[1]);
    } catch (_error) {
      return '';
    }
  }

  function syntheticGaClientId(eventId) {
    var digits = String(eventId || '').split('').reduce(function (value, character) {
      return (value * 33 + character.charCodeAt(0)) % 1000000000;
    }, 5381);
    return String(Date.now()) + '.' + String(digits || 1);
  }

  function gaTicketKey(eventId) {
    return 'bb:corporate-proposal:ga-ticket:' + eventId;
  }

  function requestGaTicket(eventId, leadId, session) {
    if (processingPermission('analyticsProcessingAllowed') !== true) {
      return Promise.resolve('');
    }
    var existing = sessionGet(session, gaTicketKey(eventId));
    if (existing) return Promise.resolve(existing);
    return window.fetch(GA_CONSUMER_URL + '/v1/ticket', {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_id: eventId, lead_id: leadId })
    }).then(function (response) {
      if (!response.ok) throw new Error('GA ticket rejected');
      return response.json();
    }).then(function (result) {
      if (!result || !result.ticket) throw new Error('GA ticket missing');
      sessionSet(session, gaTicketKey(eventId), result.ticket);
      return result.ticket;
    });
  }

  function sendGaLead(state, session, onAccepted, onConsentSkipped, onRejected) {
    var allowed = processingPermission('analyticsProcessingAllowed');
    if (allowed === false) {
      onConsentSkipped();
      return false;
    }
    if (allowed !== true) return false;

    var clientId = gaCookieValue();
    var clientIdSource = 'ga_cookie';
    if (!clientId && state.gaIdentityAttempts < GA_COOKIE_WAIT_ATTEMPTS) {
      state.gaIdentityAttempts += 1;
      return false;
    }
    if (!clientId) {
      clientId = state.syntheticGaClientId || syntheticGaClientId(state.payload.event_id);
      state.syntheticGaClientId = clientId;
      clientIdSource = 'synthetic';
    }

    requestGaTicket(state.payload.event_id, state.payload.lead_id, session)
      .then(function (ticket) {
        if (processingPermission('analyticsProcessingAllowed') !== true) {
          onConsentSkipped();
          return null;
        }
        if (!ticket) throw new Error('GA ticket unavailable');
        var gaPayload = {
          ticket: ticket,
          event_id: state.payload.event_id,
          lead_id: state.payload.lead_id,
          occurred_at_ms: state.payload.occurred_at_ms,
          client_id: clientId,
          client_id_source: clientIdSource,
          form_name: FORM_NAME,
          lead_path: LEAD_PATH,
          conversion_source: 'shopify_contact_success',
          service_type: state.payload.service_type,
          planning_window: state.payload.planning_window,
          headcount_band: state.payload.headcount_band,
          budget_band: state.payload.budget_band,
          acquisition_channel: state.payload.acquisition_channel
        };
        return window.fetch(GA_CONSUMER_URL + '/v1/lead', {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(gaPayload)
        });
      })
      .then(function (response) {
        if (!response) return;
        if (!response.ok) throw new Error('GA lead rejected');
        return response.json();
      })
      .then(function (result) {
        if (result && result.ok === true) onAccepted();
      })
      .catch(onRejected);
    return true;
  }

  function attributionFrom(params) {
    var values = {};
    ATTRIBUTION_KEYS.forEach(function (key) {
      var value = params.get(key);
      if (value) values[key] = value.slice(0, MAX_ATTRIBUTION_LENGTH);
    });
    return values;
  }

  function dimensionToken(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, MAX_DIMENSION_LENGTH);
  }

  function collectDimensions(form) {
    var dimensions = {};
    Object.keys(DIMENSION_FIELDS).forEach(function (key) {
      var field = form.querySelector(DIMENSION_FIELDS[key]);
      var value = field ? dimensionToken(field.value) : '';
      dimensions[key] = value || 'not_provided';
    });
    return dimensions;
  }

  function acquisitionChannel(params) {
    var source = dimensionToken(params.get('utm_source'));
    var medium = dimensionToken(params.get('utm_medium'));
    if (params.get('gclid') || params.get('gbraid') || params.get('wbraid')) return 'paid_search';
    if (params.get('fbclid')) return 'paid_social';
    if (source === 'google' && (medium === 'cpc' || medium === 'paid')) return 'paid_search';
    if (
      source === 'facebook' ||
      source === 'instagram' ||
      source === 'meta' ||
      medium === 'paid_social'
    ) return 'paid_social';
    if (source || medium) return 'tagged_campaign';
    return 'direct_or_unattributed';
  }

  function prefillService(form, params) {
    var requested = dimensionToken(params.get('service'));
    var select = form.querySelector(DIMENSION_FIELDS.service_type);
    if (!requested || !select || !select.options) return;
    Array.prototype.some.call(select.options, function (option) {
      if (dimensionToken(option.value) !== requested) return false;
      select.value = option.value;
      return true;
    });
  }

  function setHidden(form, selector, value) {
    var input = form.querySelector(selector);
    if (input) input.value = value || '';
  }

  function draftKey(eventId) {
    return 'bb:corporate-proposal:draft:' + eventId;
  }

  function saveDraft(form, eventId, session) {
    if (!session) return;
    var draft = {};
    Array.prototype.forEach.call(form.querySelectorAll('[data-corp-preserve]'), function (field) {
      if (!field.name) return;
      draft[field.name] = String(field.value || '').slice(0, MAX_DRAFT_VALUE_LENGTH);
    });
    try {
      sessionSet(session, draftKey(eventId), JSON.stringify(draft));
    } catch (_error) {
      /* A full/blocked session store must not interrupt submission. */
    }
  }

  function restoreErrorDraft(form, eventId, session) {
    if (!session || !eventId || !form.querySelector('[data-corp-error]')) return;
    var draft = {};
    try {
      draft = JSON.parse(sessionGet(session, draftKey(eventId)) || '{}');
    } catch (_error) {
      draft = {};
    }
    Array.prototype.forEach.call(form.querySelectorAll('[data-corp-preserve]'), function (field) {
      if (field.name && Object.prototype.hasOwnProperty.call(draft, field.name)) {
        field.value = draft[field.name];
      }
    });
    sessionRemove(session, draftKey(eventId));
  }

  function prepareForm(form, params) {
    if (!form || form.dataset.corpTrackingInit === '1') return;
    form.dataset.corpTrackingInit = '1';

    var attribution = attributionFrom(params);
    Object.keys(attribution).forEach(function (key) {
      setHidden(form, '[data-corp-attribution="' + key + '"]', attribution[key]);
    });
    prefillService(form, params);
    var session = storage();
    restoreErrorDraft(form, params.get('event_id'), session);

    var leadId = makeId('bbcf_lead');
    var eventId = makeId('bbcf_event');
    setHidden(form, '[data-corp-lead-id]', leadId);
    setHidden(form, '[data-corp-event-id]', eventId);

    var returnTo = new URL(DESTINATION_PATH, window.location.origin);
    returnTo.searchParams.set('view', 'corporate-paid-reel13-v2');
    returnTo.searchParams.set('contact_posted', 'true');
    returnTo.searchParams.set('bb_lead', SUCCESS_MARKER);
    returnTo.searchParams.set('lead_id', leadId);
    returnTo.searchParams.set('event_id', eventId);
    setHidden(form, 'input[name="return_to"]', returnTo.pathname + returnTo.search + '#proposal');

    form.addEventListener('submit', function () {
      var pending = Object.assign(
        {
          form_name: FORM_NAME,
          lead_path: LEAD_PATH,
          lead_id: leadId,
          event_id: eventId,
          occurred_at_ms: Date.now(),
          acquisition_channel: acquisitionChannel(params)
        },
        collectDimensions(form)
      );
      var session = storage();
      if (session) {
        saveDraft(form, eventId, session);
        sessionSet(session, 'bb:corporate-proposal:pending:' + eventId, JSON.stringify(pending));
      }
      /* Start the short-lived ticket while the submit navigation is in flight.
         The success page can recover the same idempotent ticket if this response
         arrives after the document unloads. */
      requestGaTicket(eventId, leadId, session).catch(function () {});
    });
  }

  function trackSuccess(root, params) {
    var success = root.querySelector('[data-corp-success]');
    if (!success || params.get('bb_lead') !== SUCCESS_MARKER) return;

    var leadId = params.get('lead_id') || '';
    var eventId = params.get('event_id') || '';
    if (!leadId || !eventId) return;

    var session = storage();
    var trackedKey = 'bb:corporate-proposal:tracked:' + eventId;
    var metaDeliveredKey = trackedKey + ':meta';
    var metaSkippedKey = trackedKey + ':meta-consent-skipped';
    var shopifyDeliveredKey = trackedKey + ':shopify';
    var gaDeliveredKey = trackedKey + ':ga';
    var gaSkippedKey = trackedKey + ':ga-consent-skipped';
    function scheduleSuccessUrlCleanup() {
      window.setTimeout(function () {
        ['contact_posted', 'bb_lead', 'lead_id', 'event_id'].forEach(function (key) {
          params.delete(key);
        });
        var cleanQuery = params.toString();
        window.history.replaceState(
          {},
          '',
          DESTINATION_PATH + (cleanQuery ? '?' + cleanQuery : '') + '#proposal'
        );
      }, SUCCESS_URL_CLEANUP_DELAY_MS);
    }
    var metaTerminal = session && (
      sessionGet(session, metaDeliveredKey) === '1' ||
      sessionGet(session, metaSkippedKey) === '1'
    );
    var gaTerminal = session && (
      sessionGet(session, gaDeliveredKey) === '1' ||
      sessionGet(session, gaSkippedKey) === '1'
    );
    if (session && metaTerminal && sessionGet(session, shopifyDeliveredKey) === '1' && gaTerminal) {
      sessionRemove(session, draftKey(eventId));
      scheduleSuccessUrlCleanup();
      return;
    }

    var pending = {};
    if (session) {
      try {
        pending = JSON.parse(sessionGet(session, 'bb:corporate-proposal:pending:' + eventId) || '{}');
      } catch (_error) {
        pending = {};
      }
    }
    if (pending.lead_id !== leadId || pending.event_id !== eventId) return;
    /* Shopify has now rendered confirmed success. Contact values are no longer
       needed for error recovery, so remove the PII-bearing draft immediately. */
    sessionRemove(session, draftKey(eventId));

    var payload = Object.assign({}, pending, {
      form_name: FORM_NAME,
      lead_path: LEAD_PATH,
      lead_id: leadId,
      event_id: eventId,
      conversion_source: 'shopify_contact_success'
    });

    /* Confirmed rendered-success telemetry fans out to three independently
       durable transports. Meta and GA respect Shopify's current marketing and
       analytics permissions; an explicit denial is recorded as consent-skipped.
       The GA API secret stays in the Cloudflare Worker, never in this theme. */
    var state = deliveryStates[eventId] || {
      payload: payload,
      meta: Boolean(metaTerminal),
      shopify: session && sessionGet(session, shopifyDeliveredKey) === '1',
      ga: Boolean(gaTerminal),
      shopifyInFlight: false,
      gaInFlight: false,
      gaIdentityAttempts: 0,
      retryTimer: null,
      attempts: 0,
      cleanupScheduled: false
    };
    deliveryStates[eventId] = state;

    function markDelivered(transport) {
      state[transport] = true;
      if (session) {
        var key = transport === 'meta'
          ? metaDeliveredKey
          : transport === 'shopify'
            ? shopifyDeliveredKey
            : gaDeliveredKey;
        sessionSet(session, key, '1');
      }
      finishIfSettled();
    }

    function markConsentSkipped(transport) {
      state[transport] = true;
      sessionSet(session, transport === 'meta' ? metaSkippedKey : gaSkippedKey, '1');
      finishIfSettled();
    }

    function finishIfSettled() {
      if (!state.meta || !state.shopify || !state.ga) return;
      if (state.retryTimer !== null) {
        window.clearTimeout(state.retryTimer);
        state.retryTimer = null;
      }
      if (session) {
        sessionSet(session, trackedKey, '1');
        sessionRemove(session, 'bb:corporate-proposal:pending:' + eventId);
        sessionRemove(session, draftKey(eventId));
        sessionRemove(session, gaTicketKey(eventId));
      }
      if (!state.cleanupScheduled) {
        state.cleanupScheduled = true;
        scheduleSuccessUrlCleanup();
      }
    }

    function scheduleRetry() {
      if (
        (state.meta && (state.shopify || state.shopifyInFlight) && (state.ga || state.gaInFlight)) ||
        state.retryTimer !== null ||
        state.attempts > Math.max(META_RETRY_LIMIT, SHOPIFY_PUBLISH_RETRY_LIMIT, GA_RETRY_LIMIT)
      ) return;
      state.retryTimer = window.setTimeout(
        attemptDelivery,
        Math.max(META_RETRY_DELAY_MS, SHOPIFY_PUBLISH_RETRY_DELAY_MS, GA_RETRY_DELAY_MS)
      );
    }

    function attemptDelivery() {
      state.retryTimer = null;
      state.attempts += 1;
      var permissions = applyConsentTerminalStates(state, session, {
        metaSkipped: function () { markConsentSkipped('meta'); },
        gaSkipped: function () { markConsentSkipped('ga'); }
      });
      if (!state.meta && permissions.marketingAllowed === true) {
        sendMetaLead(state.payload, function () { markDelivered('meta'); });
      }
      if (!state.shopify && !state.shopifyInFlight) {
        state.shopifyInFlight = true;
        var publishStarted = publishShopifyEvent(
          SUCCESS_EVENT_NAME,
          state.payload,
          function () {
            state.shopifyInFlight = false;
            markDelivered('shopify');
          },
          function () {
            state.shopifyInFlight = false;
            scheduleRetry();
          }
        );
        if (!publishStarted) state.shopifyInFlight = false;
      }
      if (!state.ga && !state.gaInFlight && permissions.analyticsAllowed === true) {
        state.gaInFlight = true;
        var gaStarted = sendGaLead(
          state,
          session,
          function () {
            state.gaInFlight = false;
            markDelivered('ga');
          },
          function () {
            state.gaInFlight = false;
            markConsentSkipped('ga');
          },
          function () {
            state.gaInFlight = false;
            scheduleRetry();
          }
        );
        if (!gaStarted) state.gaInFlight = false;
      }
      scheduleRetry();
    }
    state.wake = function () {
      if (state.meta && state.shopify && state.ga) return;
      if (state.retryTimer !== null) {
        window.clearTimeout(state.retryTimer);
        state.retryTimer = null;
      }
      state.attempts = 0;
      attemptDelivery();
    };
    attemptDelivery();
  }

  function init(scope) {
    var root = scope || document;
    var section = root.matches && root.matches('[data-corp-proposal]')
      ? root
      : root.querySelector('[data-corp-proposal]');
    if (!section) return;

    var params = new URLSearchParams(window.location.search);
    prepareForm(section.querySelector('form'), params);
    trackSuccess(section, params);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('visitorConsentCollected', function () {
    Object.keys(deliveryStates).forEach(function (eventId) {
      var state = deliveryStates[eventId];
      if (state && typeof state.wake === 'function') state.wake();
    });
  });
})();
